
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { PropType } from "vue";
import Leaflet from "leaflet";
import "leaflet.heat";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { faCar, faTrafficCone, faLink } from "@fortawesome/pro-solid-svg-icons";

import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// Webpack bundles the default Leaflet marker images under a hashed path Leaflet can't resolve on
// its own; this swaps in the bundled URLs once, at module scope (same fix used in drive-buzz-web).
delete (Leaflet.Icon.Default.prototype as any)._getIconUrl;
Leaflet.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export type LatLngTuple = [number, number];

export interface IMapPolyline {
  points: Array<LatLngTuple>;
  color: string;
  dashed?: boolean;
  isRouteStart?: boolean;
  isRouteEnd?: boolean;
}

export interface IMapMarker {
  lat: number;
  lon: number;
  color: string;
  radius?: number;
  icon?: string;
  title?: string;
  url?: string;
  taskId?: number;
  markId?: number;
}

// re-clicking the same list row still re-triggers the maps focus
export interface IMapFocusRequest {
  points: Array<LatLngTuple>;
  nonce: number;
}

const FOCUS_ZOOM = 17;

// Finish flag marking a route's end point.
const FINISH_FLAG_HTML =
  '<svg width="22" height="26" viewBox="0 0 22 26" xmlns="http://www.w3.org/2000/svg">' +
  '<line x1="3" y1="2" x2="3" y2="25" stroke="#e8e8e8" stroke-width="2" stroke-linecap="round"/>' +
  '<rect x="4" y="2" width="16" height="12" fill="#fff"/>' +
  '<g fill="#111">' +
  '<rect x="4" y="2" width="4" height="4"/><rect x="12" y="2" width="4" height="4"/>' +
  '<rect x="8" y="6" width="4" height="4"/><rect x="16" y="6" width="4" height="4"/>' +
  '<rect x="4" y="10" width="4" height="4"/><rect x="12" y="10" width="4" height="4"/>' +
  "</g></svg>";

const finishFlagIcon = Leaflet.divIcon({
  html: FINISH_FLAG_HTML,
  className: "praxis-finish-flag",
  iconSize: [22, 26],
  iconAnchor: [3, 25],
});

const DOT_ICONS: Record<string, IconDefinition> = {
  car: faCar,
  "traffic-cone": faTrafficCone,
  link: faLink,
};

function iconSvg(iconName: string): string {
  const definition = DOT_ICONS[iconName];
  if (!definition) return "";
  const [width, height, , , pathData] = definition.icon;
  const path = Array.isArray(pathData) ? pathData.join("") : pathData;
  return `<svg viewBox="0 0 ${width} ${height}" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="${path}"/></svg>`;
}

// Coloured circle with a FontAwesome icon inside, used for the start point, task marks and media/link markers
function createDotIcon(color: string, iconName: string, radius: number, variant?: "link" | "clickable"): Leaflet.DivIcon {
  const size = radius * 2;
  const dotClass = variant ? `praxis-icon-dot praxis-icon-dot--${variant}` : "praxis-icon-dot";
  return Leaflet.divIcon({
    html: `<div class="${dotClass}" style="width:${size}px;height:${size}px;background:${color};font-size:${Math.round(size * 0.55)}px;">${iconSvg(
      iconName
    )}</div>`,
    className: "",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

@Component
export default class BaseLeafletMap extends Vue {
  //  Default: Europe
  @Prop({ type: Array as unknown as PropType<LatLngTuple>, default: () => [54, 15] })
  public center!: LatLngTuple;

  @Prop({ type: Number, default: 3 })
  public zoom!: number;

  @Prop({ type: Array as PropType<Array<IMapPolyline>>, default: () => [] })
  public polylines!: Array<IMapPolyline>;

  @Prop({ type: Array as PropType<Array<IMapMarker>>, default: () => [] })
  public markers!: Array<IMapMarker>;

  // Strava-style backdrop: a density trace of previously driven points, drawn under the active route(s).
  @Prop({ type: Array as PropType<Array<LatLngTuple>>, default: () => [] })
  public heatPoints!: Array<LatLngTuple>;

  @Prop({ type: Boolean, default: true })
  public interactive!: boolean;

  @Prop({ type: Object as PropType<IMapFocusRequest | null>, default: null })
  public focusRequest!: IMapFocusRequest | null;

  private map: Leaflet.Map | null = null;
  private heatLayer: Leaflet.HeatLayer | null = null;
  private polylineLayers: Array<Leaflet.Polyline> = [];
  private pointLayers: Array<Leaflet.CircleMarker | Leaflet.Marker> = [];

  public mounted(): void {
    this.initMap();
  }

  public beforeDestroy(): void {
    this.map?.remove();
    this.map = null;
  }

  private initMap(): void {
    const container = this.$refs.mapEl as HTMLElement;
    if (!container || this.map) return;

    this.map = Leaflet.map(container, {
      zoomControl: false,
      dragging: this.interactive,
      scrollWheelZoom: this.interactive,
      doubleClickZoom: this.interactive,
      boxZoom: this.interactive,
      keyboard: this.interactive,
      // CARTO's free basemap key requires attribution to stay visible on the map (see darkLayer below).
      attributionControl: true,
      zoomSnap: 0.5,
    }).setView(this.center, this.zoom);
    this.map.attributionControl.setPrefix(false);

    // Three switchable basemaps. Straße (street) is the default
    // CARTO now requires an API key + attribution on its basemap tiles (carto.com/basemaps/apikey).
    const darkLayer = Leaflet.tileLayer(`https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=${process.env.VUE_APP_CARTO_BASEMAPS_KEY}`, {
      subdomains: "abcd",
      maxZoom: 20,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions">CARTO</a>',
    });
    const streetLayer = Leaflet.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
    });
    const satelliteLayer = Leaflet.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
      maxZoom: 19,
      attribution: "Tiles &copy; Esri",
    });

    streetLayer.addTo(this.map);

    if (this.interactive) {
      // Added explicitly (map option "zoomControl" is off above) and first, so it's the topmost
      // control in the top-right stack, ahead of the layers switcher and fullscreen button below it.
      Leaflet.control.zoom({ position: "topright" }).addTo(this.map);

      const baseMaps: Leaflet.Control.LayersObject = { Dunkel: darkLayer, Straße: streetLayer, Satellit: satelliteLayer };
      const layersControl: Leaflet.Control.Layers = Leaflet.control.layers(baseMaps, undefined, { position: "topright", collapsed: true });
      layersControl.addTo(this.map);

      this.addFullscreenControl(container);
    }

    this.renderLayers();

    setTimeout(() => {
      this.map?.invalidateSize();
    }, 0);
  }

  // Custom control (no plugin needed) using the browser's native Fullscreen API on the map's own
  // container, so Leaflet keeps rendering into the same element instead of needing a portal/teleport.
  private addFullscreenControl(container: HTMLElement): void {
    if (!this.map) return;

    const EXPAND_ICON =
      '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M1 5V1h4M11 1h4v4M15 11v4h-4M5 15H1v-4"/></svg>';
    const COLLAPSE_ICON =
      '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M5 1v4H1M11 1v4h4M15 11h-4v4M1 11h4v4"/></svg>';

    const FullscreenControl = Leaflet.Control.extend({
      onAdd: () => {
        const wrapper = Leaflet.DomUtil.create("div", "leaflet-bar leaflet-control leaflet-control-fullscreen");
        const button = Leaflet.DomUtil.create("a", "", wrapper) as HTMLAnchorElement;
        button.href = "#";
        button.title = "Vollbild";
        button.innerHTML = EXPAND_ICON;

        Leaflet.DomEvent.on(button, "click", Leaflet.DomEvent.stop).on(button, "click", () => {
          if (document.fullscreenElement) {
            document.exitFullscreen();
          } else {
            container.requestFullscreen();
          }
        });

        document.addEventListener("fullscreenchange", () => {
          button.innerHTML = document.fullscreenElement ? COLLAPSE_ICON : EXPAND_ICON;
          setTimeout(() => this.map?.invalidateSize(), 0);
        });

        return wrapper;
      },
    });

    new FullscreenControl({ position: "topright" }).addTo(this.map);
  }

  private clearLayers(): void {
    this.polylineLayers.forEach((layer) => this.map?.removeLayer(layer));
    this.polylineLayers = [];

    this.pointLayers.forEach((layer) => this.map?.removeLayer(layer));
    this.pointLayers = [];

    if (this.heatLayer) {
      this.map?.removeLayer(this.heatLayer);
      this.heatLayer = null;
    }
  }

  private renderLayers(): void {
    if (!this.map) return;

    this.clearLayers();

    if (this.heatPoints.length > 0) {
      // heatLayer wants a third "intensity" element per point; we don't have one, so every point weighs equally.
      const weightedPoints: Array<Leaflet.HeatLatLngTuple> = this.heatPoints.map((point) => [point[0], point[1], 1]);

      this.heatLayer = Leaflet.heatLayer(weightedPoints, {
        radius: 12,
        blur: 18,
        minOpacity: 0.25,
        maxZoom: 17,
        gradient: {
          0.2: "#3b0a00",
          0.45: "#a62c00",
          0.65: "#ff6a00",
          0.85: "#ffc400",
          1.0: "#ffffff",
        },
      }).addTo(this.map);
    }

    this.polylines.forEach((segment) => {
      if (segment.points.length < 2) return;

      const line = Leaflet.polyline(segment.points, {
        color: segment.color,
        weight: 4,
        opacity: 0.7,
        dashArray: segment.dashed ? "4, 8" : undefined,
        lineCap: "round",
        lineJoin: "round",
      }).addTo(this.map as Leaflet.Map);

      this.polylineLayers.push(line);

      if (segment.isRouteStart !== false) {
        this.pointLayers.push(
          Leaflet.marker(segment.points[0], {
            icon: createDotIcon("#808080", "car", 8),
            interactive: false,
            keyboard: false,
          }).addTo(this.map as Leaflet.Map)
        );
      }

      if (segment.isRouteEnd !== false) {
        this.pointLayers.push(
          Leaflet.marker(segment.points[segment.points.length - 1], {
            icon: finishFlagIcon,
            interactive: false,
            keyboard: false,
          }).addTo(this.map as Leaflet.Map)
        );
      }
    });

    this.markers.forEach((marker) => {
      const radius = marker.radius || 7;
      const hasUrl = !!marker.url;
      const hasTaskId = marker.taskId != null;
      const clickable = hasUrl || hasTaskId;

      let layer: Leaflet.CircleMarker | Leaflet.Marker;
      if (marker.icon) {
        layer = Leaflet.marker([marker.lat, marker.lon], {
          icon: createDotIcon(marker.color, marker.icon, radius, hasUrl ? "link" : hasTaskId ? "clickable" : undefined),
          interactive: clickable,
          keyboard: clickable,
        }).addTo(this.map as Leaflet.Map);
      } else {
        layer = Leaflet.circleMarker([marker.lat, marker.lon], {
          radius,
          color: "#fff",
          fillColor: marker.color,
          fillOpacity: 1,
          weight: 2,
          interactive: clickable,
        }).addTo(this.map as Leaflet.Map);
      }

      if (marker.title) {
        layer.bindTooltip(marker.title, { direction: "top", offset: [0, -radius] });
      }

      if (marker.url) {
        const url = marker.url;
        layer.on("click", () => {
          const href = /^[a-z][\w+.-]*:\/\//i.test(url) ? url : `https://${url}`;
          window.open(href, "_blank", "noopener,noreferrer");
        });
      } else if (marker.taskId != null) {
        layer.on("click", () => this.$emit("marker-click", marker));
      }

      this.pointLayers.push(layer);
    });

    const bounds = this.getBounds();
    if (bounds) {
      this.map.fitBounds(bounds, { padding: [20, 20] });
    } else {
      this.map.setView(this.center, this.zoom);
    }
  }

  private getBounds(): Leaflet.LatLngBounds | null {
    if (this.polylineLayers.length === 0 && this.pointLayers.length === 0) return null;

    const bounds = Leaflet.latLngBounds([]);
    this.polylineLayers.forEach((layer) => bounds.extend(layer.getBounds()));
    this.pointLayers.forEach((layer) => bounds.extend(layer.getLatLng()));

    return bounds.isValid() ? bounds : null;
  }

  @Watch("polylines", { deep: true })
  public onPolylinesChanged(): void {
    this.onDataChanged();
  }

  @Watch("markers", { deep: true })
  public onMarkersChanged(): void {
    this.onDataChanged();
  }

  @Watch("heatPoints", { deep: true })
  public onHeatPointsChanged(): void {
    this.onDataChanged();
  }

  @Watch("focusRequest")
  public onFocusRequestChanged(request: IMapFocusRequest | null): void {
    if (!this.map || !request || request.points.length === 0) return;

    if (request.points.length === 1) {
      this.map.flyTo(request.points[0], FOCUS_ZOOM, { duration: 0.75 });
      return;
    }

    const bounds = Leaflet.latLngBounds(request.points);
    if (bounds.isValid()) {
      this.map.flyToBounds(bounds, { padding: [40, 40], duration: 0.75 });
    }
  }

  private onDataChanged(): void {
    if (!this.map) return;
    this.renderLayers();
    setTimeout(() => {
      this.map?.invalidateSize();
    }, 0);
  }
}
